// Frontend/src/main.tsx
import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import { RouterRoot } from "./router";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "sonner";
import { PWAUpdateManager } from "./components/PWAUpdateBanner";
import { registerServiceWorker } from "./utils/sw-registration";
import { logCollector } from "./utils/log-collector";
import { initPortalCleanup } from "./utils/portal-cleanup";
import { queryClient } from "./lib/queryClient";
import { purgeLegacyCaches } from "./lib/clearCaches";
// Imported for its side effect: wires notificationStream's SSE events into
// the query cache. See notificationStreamBridge.ts.
import "./lib/notificationStreamBridge";

// Initialize portal cleanup to prevent removeChild errors during navigation
initPortalCleanup();

// Initialize log collector
// The log collector auto-enables in dev mode (import.meta.env.DEV)
// In production builds, you can enable it manually via: localStorage.setItem('dev_logs_enabled', 'true')
// Then call: logCollector.enable()

// Log app version for debugging
console.log(
  `%c🚀 CondoGestión v${__APP_VERSION__}`,
  'font-size: 14px; font-weight: bold; color: #1F3A93;'
);
console.log(`📅 Build: ${__BUILD_TIME__}`);

// Drop caches written by a build from before notification responses were
// excluded from the service worker. Shipping that fix stops new writes; it
// does not clean up what an already-installed PWA is still carrying.
void purgeLegacyCaches();

// Register service worker with update detection
registerServiceWorker((info) => {
  if (info.needsUpdate) {
    console.log('🔄 App update available!');
    // The PWAUpdateManager component will show the banner
  }
});

const container = document.getElementById("root");
if (!container) throw new Error("Root container #root not found");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <RouterRoot />
          <PWAUpdateManager />
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
    <Toaster
      position="top-right"
      duration={2000}
      closeButton
      swipeDirections={["left", "right", "top", "bottom"]}
      richColors
      toastOptions={{
        classNames: {
          success:
            "bg-emerald-600 text-white border-emerald-700/60",
          error:
            "bg-rose-600 text-white border-rose-700/60",
          info:
            "bg-blue-600 text-white border-blue-700/60",
          warning:
            "bg-amber-500 text-white border-amber-600/60",
        },
      }}
    />
  </React.StrictMode>
);
